import { type SeatLabelMap } from '../seat-labels.js';

// ---------------------------------------------------------------------------
// Golden Balls commentary
// ---------------------------------------------------------------------------
// The engine's structured events as plain English. Housekeeping events
// narrate to null and are dropped — no key=value payloads ever reach a
// player, and nothing here can reveal more than the event already did
// (the event log is globally readable, so it carries no secrets).

const money = (value: unknown): string =>
  typeof value === 'number' ? `$${Math.floor(value).toLocaleString('en-US')}` : '—';

const claim = (value: unknown): string =>
  value === 'KILLER' ? 'a KILLER' : money(value);

const PHASE_LINE: Record<string, string> = {
  nomination: 'Declare your hidden balls.',
  claim_reveal: 'Claims are on the table.',
  discussion: 'Talk it out.',
  vote: 'Votes, everyone at once.',
  elimination_reveal: 'Every hand turns over.',
  bin_or_win: 'Bin or Win.',
  pre_final: 'Last words before the final.',
  final: 'Split or Steal.',
  ended: 'That is the game.'
};

export function formatGoldenBallsEvent(
  type: string,
  payload: Record<string, unknown>,
  seatLabels?: SeatLabelMap
): string | null {
  const p = payload ?? {};
  const label = (seatId: unknown): string => {
    const id = typeof seatId === 'string' ? seatId : '';
    return (seatLabels && seatLabels[id]) || (id ? `${id.slice(0, 6)}…` : 'someone');
  };
  const list = (seatIds: unknown): string =>
    Array.isArray(seatIds) ? seatIds.map(label).join(', ') : '';

  switch (type) {
    case 'gb.phase': {
      const phase = String(p['phase'] ?? '');
      const line = PHASE_LINE[phase];
      return line ? `Round ${p['round'] ?? '?'} — ${line}` : null;
    }
    case 'gb.dealt':
      return `Balls dealt: ${p['dealtCount'] ?? '?'} out, ${p['killersInPool'] ?? 0} killers in the pool.`;
    case 'gb.claims.revealed':
      return 'Everyone has declared. Claims revealed together.';
    case 'gb.chat':
      return `${label(p['seatId'])}: “${String(p['text'] ?? '')}”`;
    case 'gb.votes.revealed': {
      const counts = (p['counts'] ?? {}) as Record<string, number>;
      const tally = Object.entries(counts)
        .map(([seatId, count]) => `${label(seatId)} ${count}`)
        .join(' · ');
      const tie = Array.isArray(p['tiedSeatIds']) && (p['tiedSeatIds'] as unknown[]).length > 1
        ? ` Tie between ${list(p['tiedSeatIds'])}, broken on front-row balls.`
        : '';
      return `Votes in — ${tally}.${tie} ${label(p['eliminatedSeatId'])} is out.`;
    }
    case 'gb.elimination.revealed': {
      const reveals = Array.isArray(p['reveals']) ? (p['reveals'] as Array<Record<string, unknown>>) : [];
      if (reveals.length === 0) return null;
      const parts = reveals.map((reveal) => {
        const balls = Array.isArray(reveal['balls'])
          ? (reveal['balls'] as Array<Record<string, unknown>>)
          : [];
        const lies = balls.filter((ball) => ball['honest'] === false).length;
        return `${label(reveal['seatId'])} ${lies === 0 ? 'told the truth' : `lied ${lies === 1 ? 'once' : `${lies} times`}`}`;
      });
      return `Hands turned over — ${parts.join(' · ')}.`;
    }
    case 'gb.binwin.started':
      return `Bin or Win begins. ${list(p['order'])} place in that order.`;
    case 'gb.binwin.placed':
      return typeof p['narration'] === 'string' ? p['narration'] : null;
    case 'gb.binwin.skip_requested':
      return `${label(p['seatId'])} wants to skip to the end.`;
    case 'gb.binwin.skipped':
      return `Skipping ahead — the last ${p['remaining'] ?? 0} balls are placed at once.`;
    case 'gb.prefinal.started':
      return `The jackpot is ${money(p['jackpot'])}. Last words before the choice.`;
    case 'gb.final.started':
      return `Split or Steal for ${money(p['jackpot'])}.`;
    case 'gb.final.committed':
      return `${label(p['seatId'])} has locked in.`;
    case 'gb.final.revealed':
      return typeof p['explanation'] === 'string' ? p['explanation'] : null;
    case 'gb.timeout':
      return `Clock ran out on ${list(p['seatIds'])}.`;
    case 'gb.skipped':
      return 'Everyone is ready — moving on.';
    // Housekeeping: value-free commit pings, config, ready pings and the
    // debrief blob. Nothing a player needs read aloud.
    case 'gb.config':
    case 'gb.claim.committed':
    case 'gb.vote.cast':
    case 'gb.ready':
    case 'gb.debrief':
      return null;
    default:
      return null;
  }
}

/** Narrate any match event: Golden Balls lines, or nothing. */
export function isGoldenBallsEvent(type: string): boolean {
  return type.startsWith('gb.');
}
